/** Janela temporária da importação: progresso por arquivo e, ao final, ver a atividade ou atribuí-la a um treino. */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError, uploadWithProgress } from "../api/client";
import type { ActivityDetail, ImportJob } from "../api/types";
import { fileSize, isPending, jobSummary } from "../lib/activities";
import { fmtLong } from "../lib/training";
import { metersToKm } from "../lib/format";
import { Link } from "../router/router";
import { Button, Modal, SelectField, useToast } from "./ui";
import { t } from "../i18n";

export type UploadState = "uploading" | "processing" | "done" | "duplicate" | "failed";
export interface UploadEntry {
  file: File;
  state: UploadState;
  progress: number; // 0–1 (envio)
  job?: ImportJob;
  error?: string;
}

const STEPS: Record<UploadState, string> = {
  uploading: t("Enviando…"),
  processing: t("Lendo o arquivo e conferindo duplicatas…"),
  done: t("Importado"),
  duplicate: t("Já importado antes"),
  failed: t("Não foi possível importar"),
};

const activityOf = (e: UploadEntry) =>
  e.job?.kind === "activity_file" ? (e.job.result.activity_id ?? e.job.result.duplicate_of) : undefined;

export function ImportProgressModal({
  orgId,
  uploadPath,
  files,
  activityHref,
  onClose,
  onChanged,
}: {
  orgId: string;
  uploadPath: string;
  files: File[];
  activityHref: (id: string) => string;
  onClose: () => void;
  onChanged: (entries: UploadEntry[]) => void;
}) {
  const toast = useToast();
  const [entries, setEntries] = useState<UploadEntry[]>(() => files.map((file) => ({ file, state: "uploading", progress: 0 })));
  const [assigning, setAssigning] = useState<string | null>(null); // id da atividade
  const alive = useRef(true);
  const started = useRef(false);
  const patch = (i: number, p: Partial<UploadEntry>) =>
    setEntries((prev) => {
      const next = prev.map((e, k) => (k === i ? { ...e, ...p } : e));
      return next;
    });

  useEffect(() => {
    alive.current = true;
    if (started.current) return;
    started.current = true;
    (async () => {
      for (let i = 0; i < files.length; i++) {
        const form = new FormData();
        form.append("file", files[i]);
        try {
          let job = await uploadWithProgress<ImportJob>(uploadPath, form, (f) => alive.current && patch(i, { progress: f }));
          patch(i, { progress: 1, state: "processing", job });
          while (isPending(job) && alive.current) {
            await new Promise((r) => setTimeout(r, 1200));
            job = await api.get<ImportJob>(`/orgs/${orgId}/imports/${job.id}`);
            if (alive.current) patch(i, { job });
          }
          const state: UploadState = job.status === "failed" ? "failed" : job.status === "duplicate" ? "duplicate" : "done";
          if (alive.current) patch(i, { job, state, error: state === "failed" ? jobSummary(job) : undefined });
        } catch (e) {
          if (alive.current) patch(i, { state: "failed", error: (e as ApiError).message });
        }
      }
    })();
    return () => {
      alive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finished = entries.every((e) => e.state === "done" || e.state === "duplicate" || e.state === "failed");
  const okCount = entries.filter((e) => e.state === "done").length;
  useEffect(() => {
    if (finished) onChanged(entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const total = entries.length;
  const doneCount = entries.filter((e) => e.state !== "uploading" && e.state !== "processing").length;

  return (
    <Modal
      open
      title={finished ? t("Importação concluída") : t("Importando {v} de {total}…", { v: doneCount + 1 > total ? total : doneCount + 1, total })}
      onClose={() => finished && onClose()}
      footer={
        <Button variant={finished ? "primary" : "ghost"} onClick={onClose} disabled={!finished}>
          {finished ? t("Fechar") : t("Aguarde…")}
        </Button>
      }
    >
      <ul className="import-progress" aria-live="polite">
        {entries.map((e, i) => {
          const act = activityOf(e);
          const bar = e.state === "uploading" ? e.progress : e.state === "processing" ? 0.9 : 1;
          return (
            <li key={i} className={`ip-item is-${e.state}`}>
              <div className="ip-head">
                <strong className="ii-name">{e.file.name}</strong>
                <span className="muted">{fileSize(e.file.size)}</span>
              </div>
              <div
                className={`ip-bar${e.state === "processing" ? " is-busy" : ""}`}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(bar * 100)}
              >
                <span style={{ width: `${Math.round(bar * 100)}%` }} />
              </div>
              <p className="ip-status">
                {STEPS[e.state]}
                {e.state === "uploading" && ` ${Math.round(e.progress * 100)}%`}
                {(e.state === "done" || e.state === "duplicate" || e.state === "failed") && e.job && (
                  <span className="muted"> — {e.error ?? jobSummary(e.job)}</span>
                )}
                {e.state === "failed" && !e.job && e.error && <span className="muted"> — {e.error}</span>}
              </p>
              {act && (e.state === "done" || e.state === "duplicate") && (
                <div className="ip-actions">
                  <Link to={activityHref(act)} className="btn btn-primary btn-sm" onClick={onClose}>
                    {t("Ver atividade")}
                  </Link>
                  {e.job?.kind === "activity_file" && e.state === "done" && !e.job.result.matched_workout_id && (
                    <Button size="sm" onClick={() => setAssigning(assigning === act ? null : act)}>
                      {t("Atribuir a um treino da planilha")}
                    </Button>
                  )}
                </div>
              )}
              {act && assigning === act && (
                <AssignWorkout
                  orgId={orgId}
                  activityId={act}
                  onDone={() => {
                    setAssigning(null);
                    toast(t("Atividade vinculada ao treino."));
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>
      {finished && okCount > 1 && <p className="muted">{t("Cada atividade foi adicionada à sua lista de atividades realizadas.")}</p>}
    </Modal>
  );
}

/** Escolha do treino publicado (±3 dias) para ligar à atividade. */
function AssignWorkout({ orgId, activityId, onDone }: { orgId: string; activityId: string; onDone: () => void }) {
  const toast = useToast();
  const path = `/orgs/${orgId}/activities/${activityId}`;
  const [detail, setDetail] = useState<ActivityDetail | null>(null);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<ActivityDetail>(path)
      .then((d) => {
        setDetail(d);
        setChoice(d.candidates[0]?.id ?? "");
      })
      .catch((e) => setError((e as ApiError).message));
  }, [path]);

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setBusy(true);
    try {
      await api.post(`${path}/link`, { planned_workout_id: choice });
      onDone();
    } catch (e) {
      toast((e as ApiError).message, "error");
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p className="muted">{error}</p>;
  if (!detail) return <p className="muted">{t("Buscando treinos da planilha…")}</p>;
  if (detail.candidates.length === 0)
    return <p className="muted">{t("Não há treino publicado sem atividade entre 3 dias antes e 3 dias depois desta data.")}</p>;
  return (
    <form className="inline-form" onSubmit={submit}>
      <SelectField label={t("Treino da planilha")} value={choice} onChange={(e) => setChoice(e.target.value)}>
        {detail.candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {fmtLong(c.date)}: {c.title}
            {c.planned_distance_m ? ` (${metersToKm(c.planned_distance_m)})` : ""}
          </option>
        ))}
      </SelectField>
      <Button type="submit" variant="primary" busy={busy} disabled={!choice}>
        {t("Vincular")}
      </Button>
    </form>
  );
}
